import type { Auth } from "firebase/auth";

/**
 * How long each of Auth's start-up requests may take before we stop waiting
 * and boot from what's on the phone.
 */
export const AUTH_BOOT_REQUEST_TIMEOUT_MS = 2500;

/**
 * The two requests Auth makes before it reports a saved sign-in: refreshing
 * the ID token (an hour old by the next morning) and re-reading the account.
 */
export function isAuthBootRequest(url: string): boolean {
  return (
    url.startsWith("https://securetoken.googleapis.com/v1/token") ||
    url.startsWith("https://identitytoolkit.googleapis.com/v1/accounts:lookup")
  );
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

/**
 * Cap how long Auth's start-up may wait on the network.
 *
 * With a saved sign-in, Auth refreshes the token and re-reads the account
 * *before* `onAuthStateChanged` fires — and Firestore holds even cached reads
 * until it does. Each request gets 30 s before Auth gives up, so on hotel or
 * ship wifi that's "online" but can't get out, or one bar in a park, the app
 * sat on "Loading..." for seconds to a minute. Truly offline was fine only
 * because the browser fails those requests at once.
 *
 * Auth treats a failed request here as a network error and keeps the saved
 * user (only a server rejection signs them out), so aborting a slow one just
 * boots from the cache sooner. The token refreshes again as soon as Firestore
 * goes online and asks for it.
 *
 * Only start-up requests are capped: once Auth has settled, every request
 * passes straight through. Call before anything uses `auth`.
 */
export function capAuthBootRequests(auth: Auth, timeoutMs = AUTH_BOOT_REQUEST_TIMEOUT_MS): void {
  if (typeof window === "undefined" || typeof window.fetch !== "function") return;
  // The Android wrapper's sign-in handoff (AuthContext) signs in during boot,
  // and its own account read must not be cut short; that launch just waits as
  // before. Read before AuthContext strips the param: it
  // imports this module's caller, so this runs first.
  if (new URLSearchParams(window.location.search).has("firebaseToken")) return;

  let booting = true;
  const original = window.fetch;

  window.fetch = function cappedFetch(input: RequestInfo | URL, init?: RequestInit) {
    if (!booting || init?.signal || !isAuthBootRequest(requestUrl(input))) {
      return original.call(window, input, init);
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    return original
      .call(window, input, { ...init, signal: controller.signal })
      .finally(() => clearTimeout(timer));
  };

  // Not restored by reassigning window.fetch: Sentry wraps it after us, and
  // putting ours back would unwrap theirs. The flag makes it a pass-through.
  void auth.authStateReady().finally(() => {
    booting = false;
  });
}
